/**
 * PostHog analytics — EU region, PHI-safe (rev v11.0.0 · Phase 0 / A4).
 *
 * WHY THIS FILE CHANGED
 * ---------------------
 * The previous client defaulted to `https://us.i.posthog.com`, enabled
 * autocapture, enabled session recording, and exposed two helpers that
 * transmitted protected health information:
 *
 *   clinicalEvents.patientViewed(patientId)   → property `patient_id`
 *   clinicalEvents.icd10Suggested(query, code) → property `query` (free text
 *                                                 that can contain a
 *                                                 patient narrative)
 *
 * Session recording is the most dangerous of the three: a doctor opening a
 * patient record produces a video of the screen, i.e. the patient's clinical
 * data rendered in the browser, shipped to a third party.
 *
 * THE RULES NOW ENFORCED
 * -----------------------
 *  1. EU endpoint only. `us.i.posthog.com` is rejected at init time.
 *  2. Autocapture disabled — no implicit DOM capture.
 *  3. Session recording disabled — no screen capture.
 *  4. Strict allow-list: only declared event names and declared properties
 *     are transmitted. Everything else is dropped, not merely renamed.
 *  5. Identifiers are hashed with a server-side salt before transmission.
 *     A raw patient id, CIN, name or free-text query never leaves the page.
 *  6. The allow-list is the single source of truth and is unit-tested.
 *
 * The hashing salt is server-generated (see docs/operations/
 * ENVIRONMENT-VARIABLES.md → VITE_ANALYTICS_HASH_SALT). Without it the
 * helper degrades to a stable per-session pseudonymous id rather than
 * sending the raw value.
 */
import posthog from "posthog-js";
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES, hashIdentifier } from "./analytics-allowlist";

const logger = {
  info: (...args: unknown[]) => console.info("[posthog]", ...args),
  warn: (...args: unknown[]) => console.warn("[posthog]", ...args),
  error: (...args: unknown[]) => console.error("[posthog]", ...args),
};

/** Only the EU region is approved for health data. */
const EU_HOST = "https://eu.i.posthog.com";

let initialized = false;

/** Values dropped by the allow-list, kept for observability of the filter
 *  itself (counts only — never the values). */
const droppedCounts: Record<string, number> = {};

function dropCounter(key: string): number {
  droppedCounts[key] = (droppedCounts[key] ?? 0) + 1;
  return droppedCounts[key];
}

/**
 * Filter an event payload against the allow-list.
 * Returns only the properties that are both allow-listed for this event
 * and safe by construction.
 */
export function filterEventProperties(
  eventName: string,
  properties?: Record<string, unknown>,
): Record<string, unknown> {
  const allowedForEvent = ANALYTICS_PROPERTIES[eventName];
  if (!allowedForEvent) {
    dropCounter(`event:${eventName}`);
    return {};
  }
  if (!properties) return {};

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue;
    if (!allowedForEvent.includes(key)) {
      dropCounter(`property:${key}`);
      continue;
    }
    // Defence in depth: even an allow-listed property must not carry a
    // free-text clinical value.
    if (typeof value === "string" && value.length > 256) {
      dropCounter(`oversize:${key}`);
      continue;
    }
    out[key] = value;
  }
  return out;
}

/** True when the event name is approved for transmission. */
export function isAllowedEvent(eventName: string): boolean {
  return Object.prototype.hasOwnProperty.call(ANALYTICS_EVENTS, eventName);
}

export function initPostHog(): void {
  if (initialized) return;

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey) {
    logger.info("PostHog disabled (no VITE_POSTHOG_API_KEY)");
    return;
  }

  const host = ((import.meta.env.VITE_POSTHOG_HOST as string) || EU_HOST).replace(/\/$/, "");
  if (!host.includes("eu.i.posthog.com")) {
    logger.error(
      `PostHog host '${host}' rejected: only the EU endpoint (${EU_HOST}) is approved for health data.`,
    );
    return;
  }

  const disableAutocapture = String(import.meta.env.VITE_POSTHOG_DISABLE_AUTOCAPTURE ?? "true") === "true";
  const disableRecording =
    String(import.meta.env.VITE_POSTHOG_DISABLE_SESSION_RECORDING ?? "true") === "true";

  try {
    posthog.init(apiKey, {
      api_host: host,
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
      // Health data: never infer events from the DOM.
      autocapture: disableAutocapture ? false : undefined,
      // Health data: never record the screen.
      session_recording: disableRecording
        ? undefined
        : { maskAllInputs: true, recordCrossOriginIframes: false },
      respect_dnt: true,
      // No cookie banner requirement when nothing is captured implicitly.
      persistence: "localStorage+cookie",
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.opt_out_capturing();
      },
    });
    initialized = true;
    logger.info(`PostHog initialized (${host}, autocapture=${!disableAutocapture}, recording=${!disableRecording})`);
  } catch (e: unknown) {
    logger.warn({ err: e instanceof Error ? e.message : String(e) }, "PostHog init failed");
  }
}

/**
 * Stable pseudonymous identifier.
 * Raw identifiers are never transmitted; the value is a salted hash so that
 * the same entity can be correlated across events without revealing identity.
 */
export function pseudonymize(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  return hashIdentifier(raw);
}

export const analytics = {
  isEnabled: () => initialized,

  /**
   * Identify a user. The id is hashed locally; `traits` are filtered by the
   * global allow-list.
   */
  identify(userId: string, traits?: Record<string, unknown>) {
    if (!initialized) return;
    const id = pseudonymize(userId);
    if (!id) return;
    const safeTraits: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(traits ?? {})) {
      if (!ANALYTICS_PROPERTIES.__person.includes(k)) {
        dropCounter(`person:${k}`);
        continue;
      }
      safeTraits[k] = v;
    }
    posthog.identify(id, safeTraits);
  },

  /** Track a custom event. Non-allow-listed events are dropped entirely. */
  track(eventName: string, properties?: Record<string, unknown>) {
    if (!initialized) return;
    if (!isAllowedEvent(eventName)) {
      dropCounter(`event:${eventName}`);
      return;
    }
    posthog.capture(eventName, filterEventProperties(eventName, properties));
  },

  /** Set person properties (without sending an event). */
  setPerson(properties: Record<string, unknown>) {
    if (!initialized) return;
    const safe: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(properties)) {
      if (!ANALYTICS_PROPERTIES.__person.includes(k)) {
        dropCounter(`person:${k}`);
        continue;
      }
      safe[k] = v;
    }
    posthog.set_person_properties(safe);
  },

  /** Check a feature flag. */
  isFeatureEnabled(flag: string): boolean {
    if (!initialized) return false;
    return posthog.isFeatureEnabled(flag);
  },

  /** Reset (on logout). */
  reset() {
    if (!initialized) return;
    posthog.reset();
  },

  /** Diagnostics for the filter itself — counts only, never values. */
  droppedCounts(): Record<string, number> {
    return { ...droppedCounts };
  },
};

/**
 * Domain-specific event helpers — clinical / ops tracking.
 *
 * NOTE the signature change: every helper now takes an already-pseudonymised
 * identifier. Callers must run the value through `pseudonymize()` first; the
 * helpers never accept a raw patient identifier.
 */
export const clinicalEvents = {
  patientViewed: (hashedPatientId: string) =>
    analytics.track("patient_record_opened", { patient_ref: hashedPatientId }),
  consultationStarted: (hashedEncounterId: string) =>
    analytics.track("consultation_started", { encounter_ref: hashedEncounterId }),
  prescriptionCreated: (drugCount: number) =>
    analytics.track("prescription_created", { drug_count: drugCount }),
  alertAcknowledged: (hashedAlertId: string, severity: string) =>
    analytics.track("alert_acknowledged", { alert_ref: hashedAlertId, severity }),
  socThreatViewed: (kind: string) => analytics.track("soc_threat_viewed", { kind }),
  /** Free-text query is NEVER transmitted — only the code and the length. */
  icd10Suggested: (_query: string, topCode: string) =>
    analytics.track("icd10_suggested", { top_code: topCode }),
  densityChanged: (density: string) => analytics.track("density_changed", { density }),
  localeChanged: (locale: string) => analytics.track("locale_changed", { locale }),
  cockpitEntered: (cockpit: "clinical" | "operations" | "security") =>
    analytics.track("cockpit_entered", { cockpit }),
  specialtyModuleOpened: (specialtySlug: string, moduleId: string) =>
    analytics.track("specialty_module_opened", { specialty_slug: specialtySlug, module_id: moduleId }),
  specialtyUxError: (specialtySlug: string, errorKind: string) =>
    analytics.track("specialty_ux_error", { specialty_slug: specialtySlug, error_kind: errorKind }),
};

export default analytics;
